import {
  clearAllTestProgress,
  getTestProgressStorage,
} from "@/lib/test-progress";

export const AUTH_HINT_COOKIE_NAME = "auth_hint";
export const AUTH_HINT_COOKIE_VALUE = "1";
const AUTH_HINT_STORAGE_KEY = "auth_hint";
const AUTH_HINT_CHANGE_EVENT = "auth_hint_change";
export const AUTH_HINT_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

function hasAuthHintCookie(cookieHeader: string | null | undefined): boolean {
  if (!cookieHeader) return false;
  return cookieHeader
    .split(";")
    .some((part) => part.trim() === `${AUTH_HINT_COOKIE_NAME}=${AUTH_HINT_COOKIE_VALUE}`);
}

export function getBrowserAuthHint(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (hasAuthHintCookie(document.cookie)) return true;
    return window.localStorage.getItem(AUTH_HINT_STORAGE_KEY) === AUTH_HINT_COOKIE_VALUE;
  } catch {
    return false;
  }
}

export function rememberBrowserAuthHint(): void {
  if (typeof window === "undefined") return;
  const hadHint = getBrowserAuthHint();
  try {
    if (window.localStorage.getItem(AUTH_HINT_STORAGE_KEY) !== AUTH_HINT_COOKIE_VALUE) {
      window.localStorage.setItem(AUTH_HINT_STORAGE_KEY, AUTH_HINT_COOKIE_VALUE);
    }
    if (!hasAuthHintCookie(document.cookie)) {
      document.cookie = `${AUTH_HINT_COOKIE_NAME}=${AUTH_HINT_COOKIE_VALUE}; path=/; max-age=${AUTH_HINT_MAX_AGE_SECONDS}; SameSite=Lax`;
    }
  } catch {
    // Storage access can be blocked; the HttpOnly session cookie remains authoritative.
  }
  if (!hadHint) window.dispatchEvent(new Event(AUTH_HINT_CHANGE_EVENT));
}

export function clearBrowserAuthHint(): void {
  if (typeof window === "undefined") return;
  const hadHint = getBrowserAuthHint();
  const storage = getTestProgressStorage();
  try {
    storage?.removeItem(AUTH_HINT_STORAGE_KEY);
  } catch {
    // Best-effort UI hint cleanup only.
  }
  try {
    document.cookie = `${AUTH_HINT_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    // Best-effort UI hint cleanup only.
  }
  if (hadHint) window.dispatchEvent(new Event(AUTH_HINT_CHANGE_EVENT));
}

export function clearBrowserSession(): void {
  clearBrowserAuthHint();
  clearAllTestProgress(getTestProgressStorage());
}

export function subscribeBrowserAuthHintChange(listener: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener(AUTH_HINT_CHANGE_EVENT, listener);
  return () => window.removeEventListener(AUTH_HINT_CHANGE_EVENT, listener);
}
